import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import SessionNotes from '../components/SessionNotes';
import LifeObjectives from '../components/LifeObjectives';
import RecommendedReadings from '../components/RecommendedReadings';
import ProfileSettings from '../components/ProfileSettings';
import WheelOfLife from '../components/WheelOfLife';
import WheelChatPanel from '../components/WheelChatPanel';
import ReflectionAnswerForm from '../components/ReflectionAnswerForm';
import WeeklyReflectionManager from '../components/WeeklyReflectionManager';
import {
  FileText, Target, BookOpen, ChevronRight, Calendar, User, Settings, LogOut, PenTool, Sun, Moon, Compass, ChevronLeft, MessageSquare
} from 'lucide-react';
import rmmbrLogo from '../assets/rmmbr.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function ClientDashboard() {
  const { user, logout, token, loading: authLoading } = useAuth();
  const { theme, toggleTheme, branding } = useTheme();
  const [data, setData] = useState(null);
  const [reflections, setReflections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('notes');
  const [selectedItem, setSelectedItem] = useState(null);
  const [notesExpanded, setNotesExpanded] = useState(true);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showReflectionForm, setShowReflectionForm] = useState(false);
  const [reflectionContent, setReflectionContent] = useState('');
  const [editingReflection, setEditingReflection] = useState(null);
  const [chatPanelOpen, setChatPanelOpen] = useState(true);
  const [wheelCurrentScores, setWheelCurrentScores] = useState([]);
  const [wheelAssessmentId, setWheelAssessmentId] = useState(null);
  const [weeklyReflections, setWeeklyReflections] = useState([]);
  const [selectedReflectionId, setSelectedReflectionId] = useState(null);
  const profileMenuRef = useRef(null);

  useEffect(() => {
    // Wait for auth to finish loading before fetching data
    if (!authLoading && user && token) {
      fetchData();
      fetchReflections();
      fetchWeeklyReflections();
    }
  }, [authLoading, user, token]);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use axios which already has the Authorization header set from AuthContext
      const response = await axios.get(`${API_URL}/client/my-data`);
      setData(response.data);
      if (response.data.sessionNotes && response.data.sessionNotes.length > 0 && !selectedItem) {
        setSelectedItem(response.data.sessionNotes[0]);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReflections = async () => {
    try {
      const response = await axios.get(`${API_URL}/client/reflections`);
      setReflections(response.data);
    } catch (error) {
      console.error('Error fetching reflections:', error);
    }
  };

  const fetchWeeklyReflections = async () => {
    try {
      const response = await axios.get(`${API_URL}/client/weekly-reflections`);
      setWeeklyReflections(response.data);

      // If there's a pending reflection (sent but not answered), select it
      const pending = response.data.find(r => !r.hasResponse);
      if (pending) {
        setSelectedReflectionId(pending.id);
      }
    } catch (error) {
      console.error('Error fetching weekly reflections:', error);
    }
  };

  const handleUpdateObjective = async (id, objective, status, category, timeline) => {
    try {
      await axios.put(`${API_URL}/client/objectives/${id}`, { objective, status, category, timeline });
      await fetchData();
    } catch (error) {
      console.error('Error updating objective:', error);
    }
  };

  const handleAddObjective = async (objectiveData) => {
    try {
      const clientId = data?.client?.id;
      if (!clientId) {
        toast('Client data not loaded');
        return;
      }
      await axios.post(`${API_URL}/objectives`, {
        client_id: clientId,
        objective: objectiveData.objective,
        category: objectiveData.category,
        timeline: objectiveData.timeline
      });
      await fetchData();
    } catch (error) {
      console.error('Error adding objective:', error);
      toast.error('Failed to add objective');
    }
  };

  const handleDeleteObjective = async (id) => {
    try {
      await axios.delete(`${API_URL}/objectives/${id}`);
      await fetchData();
    } catch (error) {
      console.error('Error deleting objective:', error);
    }
  };

  const handleSaveReflection = async () => {
    if (!reflectionContent.trim()) return;

    try {
      if (editingReflection) {
        await axios.put(`${API_URL}/client/reflections/${editingReflection.id}`, {
          content: reflectionContent
        });
      } else {
        await axios.post(`${API_URL}/client/reflections`, {
          content: reflectionContent
        });
      }

      setReflectionContent('');
      setEditingReflection(null);
      setShowReflectionForm(false);
      await fetchReflections();
    } catch (error) {
      console.error('Error saving reflection:', error);
    }
  };

  const handleEditReflection = (reflection) => {
    setEditingReflection(reflection);
    setReflectionContent(reflection.content);
    setShowReflectionForm(true);
  };

  const handleDeleteReflection = async (id) => {
    if (!window.confirm('Are you sure you want to delete this reflection?')) return;

    try {
      await axios.delete(`${API_URL}/client/reflections/${id}`);
      await fetchReflections();
    } catch (error) {
      console.error('Error deleting reflection:', error);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="flex-1 flex justify-center items-center">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4" style={{ borderColor: 'var(--border-color)', borderTopColor: 'var(--accent)' }}></div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-tertiary)' }}>Loading your data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Top Navigation Bar */}
      <nav className="border-b flex-shrink-0" style={{ backgroundColor: 'var(--bg-primary)', borderColor: 'var(--border-color)' }}>
        <div className="px-6 py-3">
          <div className="flex justify-between items-center">
            {/* Logo / Brand Name */}
            <div className="flex items-center">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={branding.brand_name || 'Logo'} className="h-5 w-auto" />
              ) : (
                <div className="text-lg font-bold" style={{ color: branding?.primary_color || '#D4A574' }}>
                  {branding?.brand_name || 'Life Architect'}
                </div>
              )}
            </div>

            {/* Theme Toggle and Profile Menu */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg transition-all duration-200"
                style={{ color: 'var(--text-tertiary)' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                  e.currentTarget.style.color = 'var(--accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--text-tertiary)';
                }}
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </button>
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-1 rounded-full transition-all duration-200 border-2 focus:outline-none"
                  style={{ borderColor: 'var(--accent)' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-secondary)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--accent-20)' }}>
                    <User className="w-5 h-5" style={{ color: 'var(--accent)' }} />
                  </div>
                </button>

                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-xl py-2 z-50 animate-in fade-in duration-200" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)', borderWidth: '1px', borderStyle: 'solid' }}>
                    <div className="px-4 py-3 border-b" style={{ borderColor: 'var(--border-color)' }}>
                      <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{user?.name}</p>
                      <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        setShowProfileSettings(true);
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center gap-3"
                      style={{ color: 'var(--text-secondary)' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }}
                    >
                      <Settings className="w-4 h-4" />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowProfileMenu(false);
                        logout();
                      }}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-400 hover:text-red-300 transition-colors flex items-center gap-3"
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {data ? (
        <div className="flex-1 flex overflow-hidden min-h-0">
          {/* Left Sidebar - Navigation */}
          <div className="w-80 border-r flex flex-col flex-shrink-0" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-tertiary)' }}>
            {/* Header */}
            <div className="px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
              <h2 className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>Navigation</h2>
            </div>

            {/* Navigation Items */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Primary Actions Section */}
              <div className="px-4 py-3 space-y-1">
                <button
                  onClick={() => {
                    if (!notesExpanded) {
                      setNotesExpanded(true);
                    }
                    setSelectedView('notes');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 group ${
                    selectedView === 'notes' && !selectedItem ? 'shadow-sm' : ''
                  }`}
                  style={{
                    backgroundColor: selectedView === 'notes' && !selectedItem ? 'var(--accent-15)' : 'transparent',
                    color: selectedView === 'notes' && !selectedItem ? 'var(--accent)' : 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (!(selectedView === 'notes' && !selectedItem)) {
                      e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!(selectedView === 'notes' && !selectedItem)) {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }
                  }}
                >
                  <ChevronRight 
                    className={`w-4 h-4 transition-transform duration-200 flex-shrink-0 ${
                      notesExpanded ? 'rotate-90' : ''
                    }`}
                  />
                  <FileText className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Session Notes</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium`}
                    style={{
                      backgroundColor: selectedView === 'notes' && !selectedItem ? 'var(--accent-20)' : 'var(--bg-secondary)',
                      color: selectedView === 'notes' && !selectedItem ? 'var(--accent)' : 'var(--text-tertiary)'
                    }}>
                    {data.sessionNotes.length}
                  </span>
                </button>

                {notesExpanded && (
                  <div className="ml-8 mt-1 space-y-0.5 border-l pl-3" style={{ borderColor: 'var(--border-color)' }}>
                    {data.sessionNotes.map((note) => (
                      <button
                        key={note.id}
                        onClick={() => {
                          setSelectedView('notes');
                          setSelectedItem(note);
                        }}
                        className="w-full text-left px-3 py-1.5 rounded-md transition-all duration-150 text-sm flex items-center gap-2"
                        style={{
                          backgroundColor: selectedItem?.id === note.id && selectedView === 'notes' ? 'var(--accent-15)' : 'transparent',
                          color: selectedItem?.id === note.id && selectedView === 'notes' ? 'var(--accent)' : 'var(--text-tertiary)'
                        }}
                        onMouseEnter={(e) => {
                          if (!(selectedItem?.id === note.id && selectedView === 'notes')) {
                            e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                            e.currentTarget.style.color = 'var(--text-secondary)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!(selectedItem?.id === note.id && selectedView === 'notes')) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = 'var(--text-tertiary)';
                          }
                        }}
                      >
                        <Calendar className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {note.session_date || new Date(note.created_at).toLocaleDateString()}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="px-4 py-2">
                <div className="border-t border-[var(--border-color)]"></div>
              </div>

              {/* Goals & Progress Section */}
              <div className="px-4 py-3 space-y-1">
                <button
                  onClick={() => {
                    setSelectedView('weeklyReflections');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'weeklyReflections' ? 'shadow-sm' : ''
                  }`}
                  style={{
                    backgroundColor: selectedView === 'weeklyReflections' ? 'var(--accent-15)' : 'transparent',
                    color: selectedView === 'weeklyReflections' ? 'var(--accent)' : 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedView !== 'weeklyReflections') {
                      e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedView !== 'weeklyReflections') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }
                  }}
                >
                  <MessageSquare className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Weekly Reflections</span>
                  {weeklyReflections.filter(r => !r.hasResponse).length > 0 && (
                    <span className="bg-[var(--accent)] text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      {weeklyReflections.filter(r => !r.hasResponse).length}
                    </span>
                  )}
                </button>
              </div>

              {/* Divider */}
              <div className="px-4 py-2">
                <div className="border-t border-[var(--border-color)]"></div>
              </div>

              {/* Resources Section */}
              <div className="px-4 py-3 space-y-1">
                <button
                  onClick={() => {
                    setSelectedView('wheel');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'wheel' ? 'shadow-sm' : ''
                  }`}
                  style={{
                    backgroundColor: selectedView === 'wheel' ? 'var(--accent-15)' : 'transparent',
                    color: selectedView === 'wheel' ? 'var(--accent)' : 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedView !== 'wheel') {
                      e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedView !== 'wheel') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }
                  }}
                >
                  <Compass className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">RMMBR Circle</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedView('readings');
                    setSelectedItem(null);
                  }}
                  className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                    selectedView === 'readings' ? 'shadow-sm' : ''
                  }`}
                  style={{
                    backgroundColor: selectedView === 'readings' ? 'var(--accent-15)' : 'transparent',
                    color: selectedView === 'readings' ? 'var(--accent)' : 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedView !== 'readings') {
                      e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                      e.currentTarget.style.color = 'var(--text-primary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedView !== 'readings') {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-secondary)';
                    }
                  }}
                >
                  <BookOpen className="w-4 h-4 flex-shrink-0" />
                  <span className="flex-1">Reading List</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{
                      backgroundColor: selectedView === 'readings' ? 'var(--accent-20)' : 'var(--bg-secondary)',
                      color: selectedView === 'readings' ? 'var(--accent)' : 'var(--text-tertiary)'
                    }}>
                    {data.readings.length}
                  </span>
                </button>

                {/* My Reflections - Hidden for now */}
                {false && (
                  <button
                    onClick={() => {
                      setSelectedView('reflections');
                      setSelectedItem(null);
                    }}
                    className={`w-full text-left px-4 py-2.5 rounded-lg transition-all duration-200 font-medium flex items-center gap-3 ${
                      selectedView === 'reflections' ? 'shadow-sm' : ''
                    }`}
                    style={{
                      backgroundColor: selectedView === 'reflections' ? 'var(--accent-15)' : 'transparent',
                      color: selectedView === 'reflections' ? 'var(--accent)' : 'var(--text-secondary)'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedView !== 'reflections') {
                        e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                        e.currentTarget.style.color = 'var(--text-primary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedView !== 'reflections') {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-secondary)';
                      }
                    }}
                  >
                    <PenTool className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">My Reflections</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{
                        backgroundColor: selectedView === 'reflections' ? 'var(--accent-20)' : 'var(--bg-secondary)',
                        color: selectedView === 'reflections' ? 'var(--accent)' : 'var(--text-tertiary)'
                      }}>
                      {reflections.length}
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Middle Panel - Detail View */}
          <div className="flex flex-col overflow-hidden min-h-0 flex-1" style={{ backgroundColor: 'var(--bg-primary)' }}>
            {/* Chat panel toggle button - only show when viewing wheel */}
            {selectedView === 'wheel' && (
              <div className="flex-shrink-0 px-8 pt-6 pb-2 flex justify-end">
                <button
                  onClick={() => setChatPanelOpen(!chatPanelOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 border border-[var(--border-color)]"
                  style={{
                    backgroundColor: 'var(--bg-secondary)',
                    color: 'var(--text-secondary)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                    e.currentTarget.style.color = 'var(--text-primary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--bg-secondary)';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }}
                >
                  {chatPanelOpen ? (
                    <>
                      <span className="text-sm font-medium">Hide AI Guide</span>
                      <ChevronRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      <ChevronLeft className="w-4 h-4" />
                      <span className="text-sm font-medium">Show AI Guide</span>
                    </>
                  )}
                </button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-8 min-h-0">
              {selectedView === 'notes' && (
                <SessionNotes
                  sessionNotes={data.sessionNotes}
                  selectedNote={selectedItem}
                  isClientView={true}
                />
              )}

              {selectedView === 'wheel' && (
                <WheelOfLife
                  clientId={user?.clientId}
                  isCoach={false}
                  onScoresChange={setWheelCurrentScores}
                  onAssessmentChange={setWheelAssessmentId}
                />
              )}

              {selectedView === 'readings' && (
                <RecommendedReadings
                  readings={data.readings || []}
                  selectedReading={selectedItem}
                  onAdd={null}
                  onUpdate={null}
                  onDelete={null}
                />
              )}

              {selectedView === 'reflections' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>My Private Reflections</h2>
                      <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Personal notes and thoughts</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowReflectionForm(!showReflectionForm);
                        setEditingReflection(null);
                        setReflectionContent('');
                      }}
                      className="flex items-center gap-2 bg-[var(--accent)] hover:bg-[var(--accent)] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                    >
                      <PenTool className="w-5 h-5" />
                      <span>{showReflectionForm ? 'Cancel' : '+ Add Reflection'}</span>
                    </button>
                  </div>

                  {showReflectionForm && (
                    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
                      <textarea
                        value={reflectionContent}
                        onChange={(e) => setReflectionContent(e.target.value)}
                        placeholder="Write your thoughts, feelings, or insights..."
                        rows={8}
                        className="w-full px-4 py-3 rounded-lg border border-[var(--border-color)] focus:outline-none focus:ring-2 font-medium resize-none"
                        style={{
                          backgroundColor: 'var(--bg-primary)',
                          color: 'var(--text-primary)',
                          borderColor: 'var(--border-color)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--accent)';
                          e.target.style.boxShadow = '0 0 0 2px var(--accent-20)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--border-color)';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <div className="mt-4 flex justify-end gap-3">
                        <button
                          onClick={() => {
                            setShowReflectionForm(false);
                            setEditingReflection(null);
                            setReflectionContent('');
                          }}
                          className="px-6 py-2.5 rounded-lg transition-all border border-[var(--border-color)] font-medium"
                          style={{
                            backgroundColor: 'var(--bg-tertiary)',
                            color: 'var(--text-primary)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--border-color)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)'}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveReflection}
                          className="px-6 py-2.5 bg-[var(--accent)] hover:bg-[var(--accent)] text-white rounded-lg font-semibold transition-all"
                        >
                          {editingReflection ? 'Update' : 'Save'} Reflection
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-4">
                    {reflections.length === 0 ? (
                      <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-12 text-center">
                        <PenTool className="w-16 h-16 mx-auto mb-4 opacity-50" style={{ color: 'var(--text-tertiary)' }} />
                        <p className="text-lg mb-2" style={{ color: 'var(--text-secondary)' }}>No reflections yet</p>
                        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Start writing to capture your thoughts and insights</p>
                      </div>
                    ) : (
                      reflections.map((reflection) => (
                        <div
                          key={reflection.id}
                          className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6 hover:border-[var(--accent)]/30 transition-all"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                              {new Date(reflection.created_at).toLocaleDateString('en-US', {
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditReflection(reflection)}
                                className="text-sm text-[var(--accent)] hover:text-[var(--accent-hover)] transition-colors font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteReflection(reflection.id)}
                                className="text-sm text-red-400 hover:text-red-300 transition-colors font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          <p className="whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--text-primary)' }}>{reflection.content}</p>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {selectedView === 'weeklyReflections' && (
                <WeeklyReflectionManager
                  clientId={user?.clientId}
                  clientName={user?.name}
                  isClientView={true}
                />
              )}
            </div>
          </div>

          {/* Right Sidebar - AI Chat Panel (only for wheel view) */}
          {selectedView === 'wheel' && (
            <div
              className={`border-l border-[var(--border-color)] flex-shrink-0 transition-all duration-300 ease-in-out overflow-hidden ${
                chatPanelOpen ? 'w-96' : 'w-0'
              }`}
              style={{ backgroundColor: 'var(--bg-tertiary)' }}
            >
              {chatPanelOpen && (
                <div className="w-96 h-full">
                  <WheelChatPanel
                    currentScores={wheelCurrentScores}
                    assessmentId={wheelAssessmentId}
                    onClose={() => setChatPanelOpen(false)}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 flex justify-center items-center">
          <div className="text-center max-w-md">
            <p className="mb-2 text-lg" style={{ color: 'var(--text-secondary)' }}>No data available</p>
            <p className="text-sm mb-4" style={{ color: 'var(--text-tertiary)' }}>
              {user?.clientId 
                ? "Your coach hasn't shared any session notes or objectives yet."
                : "Please ensure you registered with an invitation code from your coach."}
            </p>
            {user && (
              <div className="mt-4 p-4 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg text-left">
                <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>Debug Info:</p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>User ID: {user.id}</p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Client ID: {user.clientId || 'Not set'}</p>
                <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Role: {user.role}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Profile Settings Modal */}
      <ProfileSettings
        user={user}
        isOpen={showProfileSettings}
        onClose={() => setShowProfileSettings(false)}
      />
    </div>
  );
}

export default ClientDashboard;
